import { useCallback, useEffect, useState } from "react";
import i18n from "../../i18n";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { useLazyGetTranslationQuery } from "../../services/api/admin";
import { LangTranslate } from "../../types/types";

function findTranslationForLanguage(
  translations: LangTranslate[],
  lang: string
): string | null {
  const normalizedLang = lang.toLowerCase();
  const langPrefix = normalizedLang.split("-")[0];

  const match = translations.find((item) => {
    const code = item.code?.toLowerCase() ?? "";
    const language = item.language?.toLowerCase() ?? "";
    return (
      code === normalizedLang ||
      language === normalizedLang ||
      code.startsWith(langPrefix) ||
      normalizedLang.startsWith(code.split("-")[0])
    );
  });

  return match?.translate?.trim() || null;
}

export function useTranslatableText(text: string, translatable = true) {
  const [displayText, setDisplayText] = useState(text);
  const [isTranslated, setIsTranslated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [getTranslation] = useLazyGetTranslationQuery();

  useEffect(() => {
    setDisplayText(text);
    setIsTranslated(false);
  }, [text]);

  const handleTranslate = useCallback(async () => {
    if (isTranslated) {
      setDisplayText(text);
      setIsTranslated(false);
      return;
    }

    const trimmedText = text.trim();
    if (!trimmedText) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await getTranslation(trimmedText).unwrap();
      const currentLang =
        i18n.language || localStorage.getItem("i18nextLng") || "he";
      const translated = findTranslationForLanguage(response, currentLang);

      if (translated) {
        setDisplayText(translated);
        setIsTranslated(true);
      }
    } catch (err) {
      console.error(t("ERROR_IN_SERVER_CALL"), err);
    } finally {
      setIsLoading(false);
    }
  }, [getTranslation, isTranslated, text]);

  const trimmedText = text.trim();
  const showTranslateButton = translatable && !!trimmedText;
  const buttonLabel = isLoading
    ? t("LOADING_LABEL")
    : isTranslated
      ? t("SHOW_ORIGINAL")
      : t("TRANSLATE_TO_MY_LANGUAGE");

  return {
    displayText,
    isTranslated,
    isLoading,
    handleTranslate,
    showTranslateButton,
    buttonLabel,
    hasContent: !!trimmedText,
  };
}
